import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GlobalSearch } from '../components/GlobalSearch';
import { CommandPalette } from '../components/CommandPalette';
import { NotificationCenter } from '../components/NotificationCenter';
import { useAuth } from '../lib/auth-context';
import { getStoredTheme, setStoredTheme, type ThemeMode } from '../designsystem/theme';
import { useShell } from './ShellProvider';
import { TrustChip } from './TrustChip';
import { shell } from './shellClasses';

function ThemeSwitch() {
  const [mode, setMode] = useState<ThemeMode>(() => getStoredTheme());

  useEffect(() => {
    setStoredTheme(mode);
  }, [mode]);

  const cycle = () => {
    const order: ThemeMode[] = ['dark', 'light', 'system'];
    const i = order.indexOf(mode);
    setMode(order[(i + 1) % order.length]);
  };

  return (
    <button
      type="button"
      onClick={cycle}
      className={`${shell.controlIcon} ${shell.focusRing}`}
      aria-label={`Theme: ${mode}. Click to change.`}
      title={`Theme: ${mode}`}
    >
      <span className="text-xs font-medium uppercase">{mode === 'system' ? 'sys' : mode.slice(0, 1)}</span>
    </button>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!user) {
    return (
      <Link to="/login" className={`${shell.control} ${shell.focusRing} inline-flex items-center`}>
        Sign in
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`${shell.control} ${shell.focusRing} max-w-[10rem] truncate`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        {user.display_name || user.email || user.id}
      </button>
      {open && (
        <ul
          role="menu"
          className={`absolute right-0 z-50 mt-1 w-48 overflow-hidden rounded-[var(--shell-radius-md)] border ${shell.border} ${shell.surfaceElevated} shadow-lg`}
        >
          <li role="none">
            <Link
              role="menuitem"
              to="/account/profile"
              className={`block px-3 py-2 text-sm ${shell.hoverRow}`}
              onClick={() => setOpen(false)}
            >
              Profile
            </Link>
          </li>
          <li role="none">
            <Link
              role="menuitem"
              to="/account/security"
              className={`block px-3 py-2 text-sm ${shell.hoverRow}`}
              onClick={() => setOpen(false)}
            >
              Security
            </Link>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className={`w-full px-3 py-2 text-left text-sm text-[var(--shell-danger)] ${shell.hoverRow}`}
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate('/login');
              }}
            >
              Sign out
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export function ShellTopBar() {
  const {
    setMobileOpen,
    allNav,
    primaryNav,
    openCommand,
    setOpenCommand,
    refreshing,
    requestRefresh,
  } = useShell();

  const paletteItems = primaryNav.length ? [...primaryNav, ...allNav] : allNav;

  return (
    <header className={shell.topbar}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4 lg:px-6">
        <button
          type="button"
          aria-label="Open navigation menu"
          className={`${shell.controlIcon} md:hidden ${shell.focusRing}`}
          onClick={() => setMobileOpen(true)}
        >
          Menu
        </button>

        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-[var(--shell-accent)] focus:px-3 focus:py-2 focus:text-white"
        >
          Skip to main content
        </a>

        <div className="min-w-0 flex-1">
          <GlobalSearch />
        </div>

        <CommandPalette
          items={paletteItems}
          open={openCommand}
          onOpenChange={setOpenCommand}
        />

        {/* Seller / marketplace: GlobalFilterBar only (GLOBAL-FILTER-ENGINE-000001). */}
        <TrustChip />

        <button
          type="button"
          className={`${shell.controlIcon} ${shell.focusRing}`}
          aria-label="Refresh data"
          title="Refresh"
          onClick={requestRefresh}
          disabled={refreshing}
        >
          <span aria-hidden className={refreshing ? 'animate-spin' : undefined}>
            ⟳
          </span>
        </button>

        <NotificationCenter />
        <ThemeSwitch />
        <UserMenu />
      </div>
    </header>
  );
}
